import React, {useCallback, useMemo, useState} from 'react';
import {Button, Tooltip, Typography} from '@mui/material';
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import {Link, useNavigate} from 'react-router-dom';
import Box from "@mui/material/Box";
import DateRangeFilter from "../../../../filter/DateRangeFilter";
import DateTimeUtil from "../../../../../../utils/common/DateTimeUtil";
import ModalConfirm from "../../../../modal/ModalConfirm";
import CreateNewOrderModal from "./CreateNewOrderModal";
import CustomerOrderService from "../../../../../../API/orders/orders/customer/CustomerOrderService";
import {customerOrderType} from "../../../../../../data/CustomerOrderType";
import {estimateStatuses, isCustomerOrderEditLocked} from "../../../../../../data/EstimateState";
import ButtonCreate, {buttonCreateAutoSizeSx, customerPageHeaderRowSx} from "../../../../button/ButtonCreate";
import MenuItem from "@mui/material/MenuItem";
import selectionTableUtil, {normalizeSelection} from "../../../../../../utils/common/SelectionTableUtil";
import {materialEstimateUnitStates} from "../../../../../../data/MaterialEstimateUnitState";
import {resolveCustomerOrderId} from "../../../../../../utils/compositionMediaUtils";
import {isKuhniVersoMebelDomain} from "../../../../../../utils/isKuhniVersoMebelDomain";

const CustomerOrderTable = ({
                                archive,
                                compositionOrders = false,
                                data,
                                setData,
                                createType,
                                rowSelection,
                                setRowSelection,
                                handleModalAlert,
                                tableOptions,
                                tableOptionsHandle,
                            }) => {
    const navigate = useNavigate();
    const [createModalOpen, setCreateModalOpen] = useState(Boolean(createType));
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [currentEditRowData, setCurrentEditRowData] = useState({
        numberCustomer: "",
        orderType: createType || Object.entries(customerOrderType)[0][0]
    });
    const [isDelete, setIsDelete] = useState(false);
    const {onRowSelectionChange} = selectionTableUtil(data, setRowSelection)
    const currentSelection = normalizeSelection(rowSelection)
    const [loading, setLoading] = useState(false)

    const handleClose = () => {
        createType && navigate('/customer/orders')
        setCreateModalOpen(false)
        setCurrentEditRowData(null)
    }
    const handleUpdateModel = (row) => {
        setCurrentEditRowData({
            id: row.id,
            numberCustomer: row.numberCustomer,
            orderType: row.orderType
        })
        setCreateModalOpen(true)
    }
    const handleCreateModel = () => {
        setCurrentEditRowData({
            numberCustomer: "",
            orderType: Object.entries(customerOrderType)[0][0]
        })
        setCreateModalOpen(true)
    }

    const handleSubmit = (values) => {
        setLoading(true)
        CustomerOrderService.save(values)
            .then(function (response) {
                setData(prev => [...prev, response.data])
                tableOptionsHandle((prev) => ({...prev, totalElements: response?.data.length}))
                handleModalAlert("Данные добавлены", 'success')
                handleClose()
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
            .finally(() => setLoading(false))
    };

    const handleUpdate = (data) => {
        setLoading(true)
        CustomerOrderService.edit(currentEditRowData?.id, data)
            .then(function (response) {
                setData(prev => prev.map(el => el.id === response.data.id ? response.data : el))
                handleModalAlert("Данные изменены", 'success')
                handleClose()
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
            .finally(() => setLoading(false))
    };

    const sendToArchive = (row) => {
        setCurrentEditRowData(row);
        setConfirmModalOpen(true);
    }

    const handleDeleteOrder = (row) => {
        setConfirmModalOpen(true);
        setCurrentEditRowData(row)
        setIsDelete(true)
    };
    const handleSubmitConfirm = () => {
        if (isDelete) {
            CustomerOrderService.delete(currentEditRowData?.id)
                .then(function () {
                    setData(prev => prev.filter(el => el.id !== currentEditRowData?.id))
                    tableOptionsHandle((prev) => ({...prev, totalElements: data.length - 1}))
                    setCurrentEditRowData(null)
                    setConfirmModalOpen(false);
                    setIsDelete(false)
                    handleModalAlert("Данные удалены", 'success')
                })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else {
            CustomerOrderService.sendToArchiveById(currentEditRowData.id)
                .then(function () {
                    setData(prev => prev.filter(el => el.id !== currentEditRowData?.id))
                    tableOptionsHandle((prev) => ({...prev, totalElements: data.length - 1}))
                    setCurrentEditRowData(null);
                    setConfirmModalOpen(false);
                    handleModalAlert("Отправлено в архив", 'success')
                })
                .catch(function (error) {
                    handleModalAlert(error);
                })
        }
    };

    const handleCloseConfirm = () => {
        setConfirmModalOpen(false);
        setCurrentEditRowData(null)
        setIsDelete(false)
    };

    const columns = useMemo(() => {
        const getEstimateStatusLabel = (estimateState) =>
            compositionOrders
            && isKuhniVersoMebelDomain()
            && estimateState === "PERFORMER_SELECTED"
                ? "Ожидание создания заказа"
                : estimateStatuses[estimateState];

        const orderLink = (rowIndex) => {
            const row = data[rowIndex];
            if (compositionOrders) {
                const orderRouteId = resolveCustomerOrderId(row);
                return orderRouteId != null ? '/customer/orders/' + orderRouteId : '#';
            }
            return '/customer/orders/' + row.id + '/' + encodeURIComponent(row.numberCustomer) + '/' + row.year;
        };

        const baseColumns = [
            {
                id: 'numberCustomer',
                header: 'Номер заказа',
                accessorKey: 'numberCustomer',
                filterFn: 'contains',
                size: compositionOrders ? 180 : 400,
                Cell: ({cell, row}) => {
                    return (
                        <li key={cell.getValue()}>
                            <Link
                                to={orderLink(row.index)}>{cell.getValue() === null ? 'Не занумерован' : cell.getValue()}</Link>
                        </li>
                    )
                }
            },
        ];

        if (!compositionOrders) {
            baseColumns.push({
                id: 'orderType',
                header: 'Тип заказа',
                accessorKey: 'orderType',
                filterFn: 'contains',
                size: 400,
                Cell: ({cell}) => customerOrderType[cell.getValue()],
            });
        }

        baseColumns.push(
            {
                id: 'estimateState',
                header: 'Статус',
                accessorKey: 'estimateState',
                size: 400,
                Cell: ({cell}) => getEstimateStatusLabel(cell.getValue()),
                filterFn: (row, id, filterValue) => {
                    return getEstimateStatusLabel(row.original.estimateState)
                        .toLowerCase()
                        .includes(filterValue.toLowerCase())
                },

            },
            {
                accessorFn: (row) => {
                    if (row.dateOfCreate === null) {
                    } else {
                        return new Date(row.dateOfCreate)
                    }
                },
                id: 'dateOfCreate',
                header: 'Дата',
                size: compositionOrders ? 110 : 400,
                sortingFn: 'datetime',
                Cell: ({cell}) => {
                    if (cell.getValue() !== undefined) {
                        return cell.getValue().toLocaleDateString();
                    }
                },
                filterFn: (row, id, filterValue) => {
                    const startDate = filterValue?.startDate;
                    const endDate = filterValue?.endDate;
                    if (startDate && endDate) {
                        return (
                            DateTimeUtil.checkDateBefore(row.getValue(id), startDate) &&
                            DateTimeUtil.checkDateAfter(row.getValue(id), endDate)
                        );
                    }
                    return true;
                },
                Filter: ({column}) => <DateRangeFilter
                    column={column}
                />,
            },
            {
                header: 'id',
                accessorKey: 'id',
                enableHiding: false,
                enableColumnActions: false,
                enableGrouping: false,
                enableColumnFilter: false,
                enableEditing: false,
            },
        );

        return baseColumns;

    }, [compositionOrders, data, setData]);

    const menuItem = [
        {type: "edit", text: "Редактировать", handler: handleUpdateModel},
        {type: "archive", text: "Поместить в архив", handler: sendToArchive},
        {type: "delete", text: "Удалить", handler: handleDeleteOrder}
    ]

    return (
        <Box sx={{maxWidth: "1800px"}}>
            <Box sx={compositionOrders ? customerPageHeaderRowSx : {
                display: 'flex',
                justifyContent: "space-between",
                paddingBottom: "8px",
            }}>
                <Typography sx={{fontSize: "24px", fontWeight: 700}}>Заказы</Typography>
                <Box sx={{display: 'flex', gap: "8px"}}>
                    {!archive && (compositionOrders ? (
                        <ButtonCreate
                            onClick={() => navigate('/catalog')}
                            text={"Выбрать шаблон"}
                            width="auto"
                            height="auto"
                            sx={buttonCreateAutoSizeSx}
                        />
                    ) : (
                        <ButtonCreate
                            onClick={handleCreateModel}
                            width={170}
                            variant="contained"
                            text={"Создать заказ"}
                        />
                    ))}
                </Box>
            </Box>
            <MaterialReactTable
                renderTopToolbar={({table}) => {
                    return <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "7px",
                        backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0 ? theme.palette.primary.light : "transparent",
                    }}>
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: "flex"}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                }}
                selectAllMode={"all"}
                enableColumnFilterModes
                enableGrouping={false}
                enableStickyHeader={false}
                enablePinning={false}
                // muiTableProps={{
                //     sx: {
                //         tableLayout: 'fixed',
                //     },
                // }}
                muiTableBodyCellProps={{
                    sx: {
                        p: '0px 0px 0px 10px',
                        m: '0px 0px 0px 0px',
                        border: "none",
                        "&:after": {
                            display: "none",
                        },
                        "& td:after": {
                            "&:hover": {
                                backgroundColor: "red"
                            }
                        },
                        "&:not(:last-of-type)": {
                            borderRight: '1px solid #EAEBEA',

                        },
                        "&:first-of-type": {
                            padding: 0,
                            width: "50px",
                            height: "36px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: (theme) => theme.palette.text.light,
                            "& .MuiButtonBase-root": {
                                borderRadius: "4px",
                                width: "24px",
                                height: "24px",
                                opacity: 1,
                                margin: 0,
                                padding: 0,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.stroke,
                                }
                            }
                        },
                        "& li": {
                            listStyleType: "none",
                            "& a": {
                                color: "#006CCC",
                                textDecoration: "none",
                                "&:hover": {
                                    textDecoration: "underline"
                                }
                            }
                        }
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        border: '1px solid #EAEBEA',
                        backgroundColor: "#F5F6FA",
                        padding: "10px",
                        paddingBottom: " 0.2rem",
                        paddingTop: "0.2rem"
                    },
                }}
                muiTableBodyRowProps={({row}) => {
                    return ({
                        sx: () => ({
                            height: '36px',
                            backgroundColor: "white",
                            "&:hover& td:after": {
                                display: "none",
                            },
                            "&.MuiTableRow-root.Mui-selected": {
                                backgroundColor: (theme) => theme.palette.primary.light70,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            "&:hover": {
                                "& td:after": {
                                    display: "none",
                                },
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            },
                            '&:nth-of-type(even)': {
                                "&.MuiTableRow-root.Mui-selected": {
                                    backgroundColor: (theme) => theme.palette.primary.light,
                                    "&:hover": {
                                        backgroundColor: (theme) => theme.palette.primary.light50,
                                    },
                                },
                                backgroundColor: (theme) => theme.palette.primary.lightGrey,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.stroke,
                                },
                            },
                        })
                    })
                }}
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        header: "",
                        size: 3,
                        minSize: 3,
                        grow: false,
                    },
                    'mrt-row-select': {
                        size: "50px",
                        grow: false,
                    },
                }}
                columns={columns}
                data={data}
                onRowSelectionChange={onRowSelectionChange}
                state={{rowSelection: currentSelection, showGlobalFilter: true, pagination: tableOptions}}
                getRowId={(originalRow) => originalRow.id}
                selectionPadding={1}
                selectionCss={{
                    backgroundColor: 'lightblue',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: 'blue',
                    borderRadius: '4px',
                }}
                initialState={{
                    columnVisibility: {
                        id: false,
                        model: false,
                        year: false,
                    },
                    isFullScreen: false,
                    density: "compact",
                }}
                muiToolbarAlertBannerChipProps={{color: 'primary'}}
                localization={MRT_Localization_RU}
                muiSearchTextFieldProps={{
                    placeholder: `Поиск`,
                    sx: {
                        paddingTop: "5px",
                        paddingBottom: "0px",
                        "& .MuiCollapse-root": {
                            borderRadius: '12px',
                            height: 40, width: 290,
                            "& .MuiSvgIcon-root": {
                                color: "#737478"
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: 0
                            }
                        }
                    },
                    variant: 'outlined',
                }}
                renderRowActionMenuItems={({closeMenu, row}) => menuItem.map(el => {
                    const estimateState = row.original.estimateState;
                    const editLocked = isCustomerOrderEditLocked(estimateState);
                    const disabled =
                        (el.type === "edit" && editLocked)
                        || (el.type === "archive" && estimateState !== "SHIPMENT")
                        || (el.type === "delete" && estimateState !== "WAIT_OFFERS");
                    return <MenuItem
                        key={el.type}
                        disabled={disabled}
                        onClick={() => {
                            el.handler(row.original)
                            closeMenu();
                        }}
                        sx={{
                            margin: "0 8px",
                            borderRadius: "4px",
                            "&:hover": {backgroundColor: (theme) => theme.palette.primary.light}
                        }}
                    >
                        {el.text}
                    </MenuItem>
                })
                }
                onPaginationChange={(updater) => {
                    tableOptionsHandle(updater(tableOptions));
                }}
                rowCount={tableOptions.totalElements}
                enableRowSelection={archive ? false : (row) => row.original.estimateState === "SHIPMENT"}
                enableRowActions={true}
                enableToolbarInternalActions={false}
            />

            <CreateNewOrderModal
                title={currentEditRowData?.id ? "Изменить номер заказа" : "Создать заказ"}
                initValue={currentEditRowData}
                open={createModalOpen}
                onClose={handleClose}
                onSubmit={currentEditRowData?.id ? handleUpdate : handleSubmit}
                loading={loading}
            />
            <ModalConfirm
                title={isDelete ? "Подтвердить удаление" : "Подтвердить поместить в архив"}
                modalOpen={confirmModalOpen}
                handleClose={handleCloseConfirm}
                handleSubmit={handleSubmitConfirm}
                action={"Подтвердить"}
            />
        </Box>
    );
};

export default CustomerOrderTable;